import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import LandingPage from './LandingPage';
import AuthPage from './AuthPage';
import WeeklyPlanner from '../App';
import { createUserStorage } from '../lib/supabaseStorage';
import { migrateLocalStorageToUser } from './DataMigration';

export default function AppShell() {
  const { user, loading, signOut, isConfigured } = useAuth();
  const [migrating, setMigrating] = useState(false);
  const [screen, setScreen] = useState('landing'); // 'landing' | 'auth' | 'app'
  const [authMode, setAuthMode] = useState('login');

  useEffect(() => {
    if (!isConfigured) {
      setScreen('app');
      return;
    }

    if (user) {
      const userStorage = createUserStorage(user.id);

      setMigrating(true);
      migrateLocalStorageToUser(user.id, userStorage)
        .then(() => {
          window.storage = userStorage;
          setMigrating(false);
          setScreen('app');
        })
        .catch((err) => {
          console.error('Migration error:', err);
          window.storage = userStorage;
          setMigrating(false);
          setScreen('app');
        });
    } else if (!loading) {
      setScreen('landing');
    }
  }, [user, loading, isConfigured]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setScreen('landing');
  }, [signOut]);

  // Loading / checking session
  if (loading || migrating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">
            {migrating ? 'Setting up your planner...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Auth page
  if (screen === 'auth') {
    return (
      <AuthPage
        mode={authMode}
        onSwitchMode={(mode) => setAuthMode(mode)}
        onBack={() => setScreen('landing')}
      />
    );
  }

  // Planner (authenticated or Supabase not configured)
  if (screen === 'app' && (user || !isConfigured)) {
    return <WeeklyPlanner onSignOut={isConfigured ? handleSignOut : null} user={user} />;
  }

  // Landing page (default)
  return (
    <LandingPage
      onGetStarted={() => { setAuthMode('register'); setScreen('auth'); }}
      onLogin={() => { setAuthMode('login'); setScreen('auth'); }}
    />
  );
}
